import { H1, P, PSmall } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import PageWrapper from '../../components/PageWrapper';
import styled from 'styled-components';
import { useParams, useHistory } from 'react-router-dom';
import { useGetCompany } from '../../hooks/use-hydra/admin';
import { prop } from 'styled-tools';
import { CardHeader } from '../../components/ContentCard';
import { FormInput, FormLabel } from '../../components/Form';
import { SmallTooltip } from '../../components/Tooltip';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { toast } from 'react-toastify';
import Button from '../../components/elements/Button';

day.extend(utc);

const Header = ({ name }: { name?: string }) => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: '/',
      },
      {
        label: 'Companies',
        url: '/admin/companies',
      },
      ...(name ? [{ label: name, url: null }] : []),
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0px;
  padding-bottom: 125px;
`;

const LinksWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 32px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Title = styled(P)`
  font-weight: 700;
  color: ${prop('theme.colors.textPrimary')};
  margin-top: 16px;
`;

const Container = styled.div`
  padding: 20px;
  margin-top: 32px;
  min-width: max(800px, 92vw);
  border: 1px solid ${prop('theme.colors.bgActive')};
  background-color: ${prop('theme.colors.transparentBg')};
  border-radius: 8px;
  overflow-y: auto;
`;

export default function Company() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { data, isLoading, error, refetch } = useGetCompany(parseInt(id));

  const onGoToLearners = () => {
    history.push(`/admin/companies/${id}/learners`);
  };

  const onGoToCohorts = () => {
    history.push(`/admin/companies/${id}/cohorts`);
  };

  const numLearners = data?.learners.length ?? 0;

  const discussChannelName = data?.slack.discussChannel?.name
    ? `#${data?.slack.discussChannel?.name}`
    : '';
  const statusChannelName = data?.slack.statusChannel?.name
    ? `#${data?.slack.statusChannel?.name}`
    : '';

  return (
    <PageWithAsyncData isLoading={isLoading} error={error} retry={refetch}>
      <Header name={data?.name} />
      <PageWrapper>
        <H1>{data?.name}</H1>
        <Content>
          <LinksWrapper>
            <LinkCard
              title="Learners"
              onPress={onGoToLearners}
              tooltip="Add learners to the company, and assign them to cohorts."
              icon="fa-solid fa-users"
              subtitle={`${numLearners} learners`}
            />
            <LinkCard
              title="Cohorts"
              onPress={onGoToCohorts}
              tooltip="Create cohorts, send invites, and assign mentors."
              icon="fa-solid fa-calendar-days"
              subtitle={`${data?.cohorts.length} cohorts`}
            />
            <LinkCard
              title="Stats"
              onPress={() => toast.info('Not implemented yet.')}
              isDisabled
              tooltip="Not implemented yet"
              icon="fa-solid fa-chart-line"
              subtitle={`${data?.cohorts?.[0]?.learners.length} learners in the latest cohort`}
            />
            <LinkCard
              title="Mentors"
              onPress={() => toast.info('Not implemented yet.')}
              isDisabled
              tooltip="Not implemented yet"
              icon="fa-solid fa-chalkboard-user"
              subtitle={`X mentors available`}
            />
          </LinksWrapper>
          <Container>
            <Title>Company Info</Title>
            <Row>
              <Column>
                <FormLabel>Slug</FormLabel>
                <FormInput
                  value={data?.slug ?? ''}
                  isDisabled
                  onChange={() => {}}
                  placeholder="Slug"
                />
              </Column>
              <Column>
                <FormLabel>Created at</FormLabel>
                <FormInput
                  value={day.utc(data?.createdAt).format('DD/MM/YYYY HH:mm')}
                  isDisabled
                  onChange={() => {}}
                  placeholder="Created at"
                />
              </Column>
            </Row>
            <Row>
              <Column>
                <FormLabel>Slack #discuss channel</FormLabel>
                <FormInput
                  value={discussChannelName}
                  isDisabled
                  onChange={() => {}}
                  placeholder="Missing #discuss channel"
                />
              </Column>
              <Column>
                <FormLabel>Slack #status channel</FormLabel>
                <FormInput
                  value={statusChannelName}
                  isDisabled
                  onChange={() => {}}
                  placeholder="Missing #status channel"
                />
              </Column>
            </Row>
            <Button
              label="Save"
              isDisabled
              type="primary"
              showIconBeforeLabel
              faIcon="fa-solid fa-save"
              tooltip="Not implemented yet"
            />
          </Container>
        </Content>
      </PageWrapper>
    </PageWithAsyncData>
  );
}

const Card = styled.div<{ isDisabled?: boolean }>`
  flex: 1;
  padding: 0;
  display: flex;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${prop('theme.colors.bgActive')};
  transition: all 0.3s ease-in-out;
  opacity: 0.8;
  border: 1px solid ${prop('theme.colors.bgActive')};
  ${({ isDisabled }) => isDisabled && `opacity: 0.6;`};

  &:hover {
    opacity: 1;

    ${({ isDisabled }) => (isDisabled ? `opacity: 0.7;` : `cursor: pointer;`)}
  }
`;

const CardContent = styled.div`
  padding: 20px;
  flex: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px;
`;

const CardIcon = styled.i`
  font-size: 28px;
  margin-top: 8px;
  color: ${prop('theme.colors.textRegular')};
`;

const CardSubtitle = styled(PSmall)`
  margin-top: 0;
  color: ${prop('theme.colors.textRegular')};
  padding-bottom: 0;
`;

const LinkCard = ({
  title,
  onPress,
  isDisabled,
  tooltip,
  icon,
  subtitle,
}: {
  title: string;
  onPress: () => void;
  isDisabled?: boolean;
  tooltip?: string;
  icon?: string;
  subtitle?: string;
}) => {
  return (
    <>
      <Card
        isDisabled={isDisabled}
        onClick={onPress}
        className={`card-tooltip-${title}`}
      >
        <CardContent>
          {icon && <CardIcon className={icon} />}
          <CardHeader style={{ marginTop: 8 }}>{title}</CardHeader>
          {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
        </CardContent>
      </Card>
      {tooltip && (
        <SmallTooltip
          noArrow
          place="top"
          anchorSelect={`.card-tooltip-${title}`}
        >
          {tooltip}
        </SmallTooltip>
      )}
    </>
  );
};
